import { FC, useEffect, useState } from 'react'
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { useCurrency } from '~/src/hooks'
import { formatNumber } from '~/src/libs'
import { formatMoney } from '~/src/utils/formatMoney'
import { IContainerResponse } from '~/src/repositories/Container/schema/Container'
import { formatDataStringParam } from '~/src/utils/formatDate'

interface ITableItemDetail {
  id: number
  refNo: string
  productCode: string
  productName: string
  maker: string
  carName: string
  vehicleModel: string
  engineModel: string
  transmission: string
  quantity: number
  totalSales: string
  weight: string
  orderDate: string
  status: string
  confirmationDate: string
  remarks: string
  dismantlingCharge: string
  packingCost: string
  productionInstructionUrl: string
  withCatalyst: boolean
}

interface IColumn {
  id:
    | 'id'
    | 'refNo'
    | 'productCode'
    | 'productName'
    | 'maker'
    | 'carName'
    | 'vehicleModel'
    | 'engineModel'
    | 'transmission'
    | 'quantity'
    | 'totalSales'
    | 'weight'
    | 'orderDate'
    | 'status'
    | 'confirmationDate'
    | 'remarks'
    | 'dismantlingCharge'
    | 'packingCost'
    | 'withCatalyst'
  label: string
  minWidth?: number
  align?: 'right'
  render: (item: ITableItemDetail, index: number) => React.ReactNode
}

interface ITableItemDetailProps {
  data?: IContainerResponse['data']['containerItems']
  totalPrice?: number
}

export const TableItemDetail: FC<ITableItemDetailProps> = ({ data, totalPrice }): JSX.Element => {
  const { t } = useTranslation()
  const { baseCurrencyUnit, unitPosition } = useCurrency()

  const columns: IColumn[] = [
    {
      id: 'refNo',
      label: `${t('container_order_detail.ref_no.')}`,
      minWidth: 110,
      render: ({ refNo, productionInstructionUrl }) => {
        return productionInstructionUrl ? (
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'Roboto',
              fontStyle: 'normal',
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.15px',
            }}
          >
            <Link href={`${productionInstructionUrl}`} underline="hover" target="_blank" rel="noreferrer">
              {refNo}
            </Link>
          </Typography>
        ) : (
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'Roboto',
              fontStyle: 'normal',
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.15px',
            }}
          >
            {refNo}
          </Typography>
        )
      }
    },
    {
      id: 'productName',
      label: `${t('container_order_detail.product_name')}`,
      minWidth: 163,
      render: ({ productName }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {productName}
        </Typography>
      ),
    },
    {
      id: 'quantity',
      label: `${t('container_order_detail.number_of_orders')}`,
      minWidth: 71,
      render: ({ quantity }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {quantity}
        </Typography>
      ),
    },
    {
      id: 'totalSales',
      label: `${t('container_order_detail.total_sales')}`,
      minWidth: 163,
      render: ({ totalSales }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {formatNumber(Number(totalSales))}
        </Typography>
      ),
    },
    {
      id: 'packingCost',
      label: `${t('container_order_detail.packing_cost')}`,
      minWidth: 113,
      render: ({ packingCost }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {formatNumber(Number(packingCost))}
        </Typography>
      ),
    },
    {
      id: 'dismantlingCharge',
      label: `${t('container_order_detail.dismantle_fee')}`,
      minWidth: 113,
      render: ({ dismantlingCharge }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {formatNumber(Number(dismantlingCharge))}
        </Typography>
      ),
    },
    {
      id: 'weight',
      label: `${t('container_order_detail.weight')}`,
      minWidth: 80,
      render: ({ weight }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {formatNumber(Number(weight))}
        </Typography>
      ),
    },
    {
      id: 'carName',
      label: `${t('container_order_detail.car_name')}`,
      minWidth: 163,
      render: ({ carName }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {carName}
        </Typography>
      ),
    },
    {
      id: 'vehicleModel',
      label: `${t('container_order_detail.vehicle_model')}`,
      minWidth: 163,
      render: ({ vehicleModel }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {vehicleModel}
        </Typography>
      ),
    },
    {
      id: 'engineModel',
      label: `${t('container_order_detail.engine_model')}`,
      minWidth: 113,
      render: ({ engineModel }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {engineModel}
        </Typography>
      ),
    },
    {
      id: 'transmission',
      label: `${t('container_order_detail.drive_1_/_tm_/_drive_2')}`,
      minWidth: 163,
      render: ({ transmission }) => (
        <Typography
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {transmission}
        </Typography>
      ),
    },
    {
      id: 'withCatalyst',
      label: `${t('container_order_detail.catalyst')}`,
      minWidth: 163,
      render: ({ withCatalyst }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {withCatalyst === null ? "" : withCatalyst ? `${t('container_order_detail.catalyst_yes')}` : `${t('container_order_detail.catalyst_no')}` }
          </Typography>
      ),
    },
    {
      id: 'orderDate',
      label: `${t('container_order_detail.order_date')}`,
      minWidth: 163,
      render: ({ orderDate }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {formatDataStringParam(orderDate)}
        </Typography>
      ),
    },
    {
      id: 'confirmationDate',
      label: `${t('container_order_detail.appointment_date')}`,
      minWidth: 163,
      render: ({ confirmationDate }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {formatDataStringParam(confirmationDate)}
        </Typography>
      ),
    },
    {
      id: 'status',
      label: `${t('container_order_detail.status')}`,
      minWidth: 163,
      render: ({ status }) => {
        return (
          status && (
            <Typography
              component={'span'}
              variant="body2"
              sx={{
                fontFamily: 'Roboto',
                fontStyle: 'normal',
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0.15px',
                color: 'rgba(0, 0, 0, 0.87)',
                border: '1px solid #e0e0e0',
                padding: '3px 10px',
                borderRadius: '15px',
              }}
            >
              {status}
            </Typography>
          )
        )
      },
    },
  ]

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)

  const handleChangePage = (event: unknown, newPage: number): void => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  return (
    <Paper sx={{ width: '100%', mb: 2 }}>
      <Box sx={{ padding: '16px', boxShadow: 'inset 0px -1px 0px rgba(0, 0, 0, 0.12)' }}>
        <Typography
          variant={'h6'}
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '20px',
            lineHeight: '32px',
            letterSpacing: '0.15px',
            color: '#000000',
            marginBottom: '8px',
          }}
        >
          {t('container_order_detail.item_details')}
        </Typography>
        <Stack direction="row" spacing={1} alignItems={'center'}>
          <Typography
            sx={{
              fontFamily: 'Roboto',
              fontStyle: 'normal',
              fontSize: '14px',
              lineHeight: '14px',
              color: '#000000',
            }}
          >
            {t('container_order_detail.total_price')}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Roboto',
              fontStyle: 'normal',
              fontWeight: 700,
              fontSize: '20px',
              lineHeight: '20px',
              color: '#F23D3D',
            }}
          >
            {formatMoney(totalPrice, baseCurrencyUnit, unitPosition)}
          </Typography>
        </Stack>
      </Box>
      <TableContainer>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth, background: '#FFFFFF' }}
                  sx={index === 0 ? { padding: '16px 8px 16px 16px' } : { padding: '16px 8px' }}
                >
                  <Typography
                    sx={{
                      borderRight: 2,
                      borderColor: `${index === columns.length - 1 ? 'white' : '#E0E0E0'}`,
                      fontSize: 14,
                      lineHeight: '24px',
                      fontStyle: 'normal',
                      fontWeight: 'medium',
                    }}
                  >
                    {column.label}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data
              ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((item, index) => {
                return (
                  <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                    {columns.map((column, i) => {
                      return (
                        <TableCell
                          key={i}
                          align={column.align}
                          sx={i === 0 ? { padding: '16px 8px 16px 16px' } : { padding: '16px 8px' }}
                        >
                          {column.render(item, index)}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={data ? data.length : 0}
        rowsPerPage={rowsPerPage}
        sx={{
          fontSize: '12px',
          fontStyle: 'normal',
          lineHeight: '20px',
          fontWeight: 'Regular',
        }}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  )
}
